import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ArrowRight } from 'lucide-react'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

export const Route = createFileRoute('/_site/blog/multi-region-consensus')({
  component: MultiRegionConsensusPage,
  head: () => ({
    meta: [
      { title: 'Multi-Region Check Consensus on the BEAM' },
      { name: 'description', content: 'How several check locations confirm a failure before Uptrack alerts (archived; written for the Elixir version).' },
      { property: 'og:title', content: 'How We Built Multi-Region Check Consensus on the BEAM' },
      { property: 'og:description', content: 'How several check locations confirm a failure before Uptrack alerts (archived; written for the Elixir version).' },
      { property: 'og:image', content: 'https://uptrack.app/og/blog-multi-region-consensus.png' },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: 'https://uptrack.app/blog/multi-region-consensus' },
      { property: 'article:published_time', content: '2026-04-05' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/blog/multi-region-consensus' },
    ],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: 'How We Built Multi-Region Check Consensus on the BEAM',
          description: 'How several check locations confirm a failure before Uptrack alerts (archived; written for the Elixir version).',
          datePublished: '2026-04-05',
          dateModified: '2026-05-22',
          author: { '@type': 'Organization', name: 'Uptrack' },
          publisher: { '@type': 'Organization', name: 'Uptrack', url: 'https://uptrack.app', logo: { '@type': 'ImageObject', url: 'https://uptrack.app/uptrack-logo-header.png' } },
          mainEntityOfPage: 'https://uptrack.app/blog/multi-region-consensus',
        }),
      },
    ],
  }),
})

function MultiRegionConsensusPage() {
  return (
    <>

      <>
        <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
          <p className="mb-6 rounded-lg border border-border bg-muted p-4 text-lg leading-[1.7] text-body">
        Archived, October 2026: this post was written when Uptrack ran on Elixir. Since June 2026 the backend is
        Rust, so the Elixir details below are historical. {MONITOR_LOCATIONS.archived} See{' '}
        <Link to="/blog/22-check-locations" className="text-primary hover:underline">checking your site from 24 locations</Link>.
      </p>
      {/* Header */}
          <div className="mb-12">
            <p className="mb-4 text-sm text-primary">Engineering</p>
            <h1 className="font-sans mb-4 text-4xl font-semibold tracking-tight md:text-5xl">
              How we built multi-region check consensus on the BEAM
            </h1>
            <p className="mb-6 text-lg leading-[1.7] text-body">
              Checks from 3 continents must agree before we wake you up. Zero external dependencies — just OTP primitives that Ericsson battle-tested for 30 years.
            </p>
            <p className="text-sm text-muted-foreground">April 5, 2026 · 12 min read</p>
          </div>

          {/* The Problem */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">The 3am problem</h2>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              UptimeRobot checks your site from one location. A CDN edge goes down in Frankfurt. Your server in Virginia is fine. Your users in Tokyo see no issues. But the single check from Frankfurt fails, and your phone buzzes at 3am.
            </p>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              This is the false alert problem. Single-region monitoring can't distinguish between "the internet is broken between point A and point B" and "your server is actually down."
            </p>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              The fix sounds simple: check from multiple regions, only alert if most agree. But the implementation is surprisingly hard. You need to coordinate checks across continents, collect results in real-time, compute consensus, and avoid duplicate alerts — all without adding latency or single points of failure.
            </p>
            <p className="text-lg leading-[1.7] text-body">
              Here's how we did it with zero external dependencies using Erlang/OTP primitives.
            </p>
          </section>

          {/* Architecture Overview */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">The architecture: one process per monitor</h2>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              Uptrack follows the Discord/WhatsApp pattern: one BEAM process per long-lived entity. Each monitor gets its own GenServer that self-schedules checks via <code className="rounded bg-muted px-1.5 py-0.5 text-sm">Process.send_after</code>.
            </p>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              For multi-region, the same monitor runs on every node. Three continents, three processes, one monitor:
            </p>
            <pre tabIndex={0} className="mb-4 overflow-x-auto rounded-lg border border-border bg-muted p-4 text-sm leading-relaxed"><code className="text-body">{`┌─────────────────────────────────────────────────────────┐
│              Erlang Cluster (Tailscale mesh)             │
│                                                         │
│  EU (Germany)      Asia (India)      US (Virginia)      │
│  ┌────────────┐   ┌────────────┐   ┌────────────┐      │
│  │MonitorProcess│  │MonitorProcess│  │MonitorProcess│     │
│  │ id: "abc"  │   │ id: "abc"  │   │ id: "abc"  │      │
│  │ Gun → target│  │ Gun → target│  │ Gun → target│      │
│  │ pg group   │◄─►│ pg group   │◄─►│ pg group   │      │
│  └────────────┘   └────────────┘   └────────────┘      │
│                                                         │
│  50K monitors      50K monitors     50K monitors        │
│  each self-        each self-       each self-          │
│  scheduling        scheduling       scheduling          │
└─────────────────────────────────────────────────────────┘`}</code></pre>
            <p className="text-lg leading-[1.7] text-body">
              Each process holds a persistent Gun HTTP connection to the target. TLS handshake happens once at startup — not on every check. A 30-second check cycle takes ~50ms of actual HTTP time.
            </p>
          </section>

          {/* pg Process Groups */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">How pg groups connect the continents</h2>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              <code className="rounded bg-muted px-1.5 py-0.5 text-sm">pg</code> is an OTP module that's been in Erlang since OTP 23. It manages process groups across distributed nodes. Tested at 5,000 nodes and 150,000 processes by the OTP team.
            </p>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              When a MonitorProcess starts, it joins a pg group named after its monitor ID:
            </p>
            <pre tabIndex={0} className="mb-4 overflow-x-auto rounded-lg border border-border bg-muted p-4 text-sm leading-relaxed"><code className="text-status-up">{`# On init, each MonitorProcess joins the group
:pg.join(:monitor_checks, monitor_id, self())

# After checking, broadcast result to all group members
:pg.get_members(:monitor_checks, monitor_id)
|> Enum.each(&send(&1, {:region_result, @region, result}))`}</code></pre>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              That's it. No message broker, no Redis pub/sub, no database polling. Erlang distribution delivers messages across continents in microseconds. When a node dies, pg automatically removes its processes from all groups.
            </p>
            <p className="text-lg leading-[1.7] text-body">
              The elegance: each MonitorProcess doesn't know or care how many regions exist. It broadcasts to the group and receives from the group. Add a 4th region, and it just works — the new process joins the pg group, and everyone sees it.
            </p>
          </section>

          {/* Consensus Timeline */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">The consensus timeline: sub-second across 3 continents</h2>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              Here's what happens during a single check cycle for a monitor where the site is down in Asia but up everywhere else:
            </p>
            <pre tabIndex={0} className="mb-4 overflow-x-auto rounded-lg border border-border bg-muted p-4 text-sm leading-relaxed"><code className="text-body">{`T=0.0s  EU checks target ──→ "up" ──→ pg broadcast to Asia, US
T=0.2s  Asia checks target ──→ "down" ──→ pg broadcast to EU, US
T=0.5s  US checks target ──→ "up" ──→ pg broadcast to EU, Asia

T=0.5s  EU has: EU=up, Asia=down, US=up → 2/3 up → ✅ No alert
T=0.5s  Asia has: EU=up, Asia=down, US=up → 2/3 up → (silent)
T=0.5s  US has: EU=up, Asia=down, US=up → 2/3 up → (silent)

                    Total consensus time: ~0.5 seconds
              CDN blip in Asia? Your phone stays silent.`}</code></pre>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              Now compare with a real outage:
            </p>
            <pre tabIndex={0} className="mb-4 overflow-x-auto rounded-lg border border-border bg-muted p-4 text-sm leading-relaxed"><code className="text-body">{`T=0.0s  EU checks ──→ "down" ──→ broadcasts
T=0.3s  Asia checks ──→ "down" ──→ broadcasts
T=0.5s  US checks ──→ "down" ──→ broadcasts

T=0.5s  All 3 nodes: 3/3 down → consensus = DOWN
        Home node (EU) increments consecutive_failures
        After 3 consecutive → 🚨 ALERT

    Total: 30s interval × 3 confirmations + 0.5s consensus
         = ~91 seconds from outage to alert
         = confirmed from 3 continents`}</code></pre>
            <p className="text-lg leading-[1.7] text-body">
              A false alert would require: the majority of regions failing, multiple consecutive times. The probability of that happening by chance is essentially zero.
            </p>
          </section>

          {/* Home Node */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">The home node: who gets to press the button?</h2>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              All 3 nodes compute the same consensus. But only one should trigger the alert — otherwise you'd get 3 Slack messages for every incident.
            </p>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              We use deterministic hash-based assignment. The "home node" for each monitor is computed from its ID:
            </p>
            <pre tabIndex={0} className="mb-4 overflow-x-auto rounded-lg border border-border bg-muted p-4 text-sm leading-relaxed"><code className="text-status-up">{`defp home_node?(monitor_id) do
  nodes = [node() | Node.list()] |> Enum.sort()
  hash = :erlang.phash2(monitor_id, length(nodes))
  Enum.at(nodes, hash) == node()
end

defp maybe_trigger_alert(state) do
  if home_node?(state.monitor_id) do
    # Only this node sends alerts
    do_alert(state)
  else
    # Other nodes track state but stay silent
    state
  end
end`}</code></pre>
            <p className="text-lg leading-[1.7] text-body">
              The hash is deterministic — same monitor ID always maps to the same node. Monitors are distributed evenly: 900 monitors across 3 nodes = ~300 "home" per node. If a node dies, the hash redistributes to survivors.
            </p>
          </section>

          {/* Edge Cases */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Edge cases that would break a naive implementation</h2>

            <h3 className="font-sans mb-2 mt-6 text-lg font-semibold">Slow region</h3>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              Asia's check takes 8 seconds (congested submarine cable). EU and US finished in 200ms.
            </p>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              Solution: 10-second timeout. If a region doesn't respond, it's excluded from consensus — not counted as up OR down. 2/2 is still valid consensus.
            </p>

            <h3 className="font-sans mb-2 mt-6 text-lg font-semibold">Node crash</h3>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              The India node loses power.
            </p>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              pg automatically removes dead processes from groups. EU and US continue with 2-node consensus. When India comes back, its processes rejoin the pg groups on startup. No manual intervention.
            </p>

            <h3 className="font-sans mb-2 mt-6 text-lg font-semibold">Network partition (netsplit)</h3>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              The submarine cable between EU and Asia is cut. EU can still talk to US, but Asia is isolated.
            </p>
            <pre tabIndex={0} className="mb-4 overflow-x-auto rounded-lg border border-border bg-muted p-4 text-sm leading-relaxed"><code className="text-body">{`EU ←→ US: connected (2 nodes visible)
   ✗
Asia: isolated (1 node visible)

EU consensus:  2/2 results → works normally
Asia consensus: 1/1 result → local only, skip consensus

Partition heals → pg reconnects → 3 nodes again`}</code></pre>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              Each side of the partition operates independently. No split-brain alerts because consensus requires majority of <em>visible</em> nodes.
            </p>

            <h3 className="font-sans mb-2 mt-6 text-lg font-semibold">Duplicate alerts</h3>
            <p className="text-lg leading-[1.7] text-body">
              All 3 nodes compute "DOWN". Without guard rails, all 3 would fire alerts. The <code className="rounded bg-muted px-1.5 py-0.5 text-sm">home_node?</code> function ensures exactly one node acts on the consensus.
            </p>
          </section>

          {/* Why not a database? */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Why not just use the database?</h2>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              The obvious approach: each region writes its check result to PostgreSQL. A periodic query aggregates results and computes consensus.
            </p>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              We tried this. Three problems:
            </p>
            <div className="mb-4 space-y-4">
              <div className="rounded-lg border border-border bg-muted p-4">
                <h3 className="mb-1 font-semibold text-status-down">1. Staleness</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Each region checks at slightly different times (jitter). When EU reads Asia's result, it might get the <em>previous</em> check from 30 seconds ago — not the current one. Average staleness: ~15 seconds. That defeats the purpose of multi-region consensus.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-muted p-4">
                <h3 className="mb-1 font-semibold text-status-down">2. Advisory lock issues</h3>
                <p className="text-lg leading-[1.7] text-body">
                  A singleton aggregator needs leader election. PostgreSQL advisory locks are the standard approach, but they don't work reliably with PgBouncer in transaction pooling mode — connections get swapped, locks get released unexpectedly.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-muted p-4">
                <h3 className="mb-1 font-semibold text-status-down">3. The irony</h3>
                <p className="text-lg leading-[1.7] text-body">
                  We moved checks OUT of the database (GenServer-per-monitor) to remove the Oban bottleneck and get 100K+ concurrent checks. Putting consensus BACK in the database would reintroduce the exact bottleneck we eliminated.
                </p>
              </div>
            </div>
            <p className="text-lg leading-[1.7] text-body">
              pg messages solve all three: zero staleness (real-time delivery), no leader election needed (each process computes independently), and no database in the hot path.
            </p>
          </section>

          {/* Benchmark */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Benchmark: 100K checks on a $10/mo server</h2>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              We benchmarked on a single Netcup RS 1000 (4 AMD EPYC cores, 8GB RAM, $10/mo):
            </p>
            <div className="mb-4 overflow-x-auto" tabIndex={0} role="region" aria-label="Scrollable table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="pb-2 pr-4 text-left font-medium text-muted-foreground">Concurrent</th>
                    <th className="pb-2 pr-4 text-left font-medium text-muted-foreground">Checks/sec</th>
                    <th className="pb-2 pr-4 text-left font-medium text-muted-foreground">P50</th>
                    <th className="pb-2 text-left font-medium text-muted-foreground">Failures</th>
                  </tr>
                </thead>
                <tbody className="text-body">
                  <tr className="border-b border-white/5"><td className="py-2 pr-4">10,000</td><td className="py-2 pr-4">2,602</td><td className="py-2 pr-4">1.6s</td><td className="py-2">0</td></tr>
                  <tr className="border-b border-white/5"><td className="py-2 pr-4">50,000</td><td className="py-2 pr-4">2,782</td><td className="py-2 pr-4">8.2s</td><td className="py-2">0</td></tr>
                  <tr className="border-b border-white/5"><td className="py-2 pr-4 font-semibold text-status-up">100,000</td><td className="py-2 pr-4 font-semibold text-status-up">2,602</td><td className="py-2 pr-4">16.9s</td><td className="py-2 font-semibold text-status-up">0</td></tr>
                  <tr><td className="py-2 pr-4">110,000</td><td className="py-2 pr-4">2,444</td><td className="py-2 pr-4">19.1s</td><td className="py-2">0</td></tr>
                </tbody>
              </table>
            </div>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              110,000 concurrent HTTP checks, zero failures, on hardware that costs less than a Netflix subscription. The BEAM was designed for this — Ericsson used the same architecture to handle millions of concurrent phone calls.
            </p>
            <p className="text-lg leading-[1.7] text-body">
              With 4 nodes across 3 continents, multi-region consensus supports ~440K monitors. At 10 monitors per user, that's 44,000 users before needing a 5th node.
            </p>
          </section>

          {/* The Code */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">The code: surprisingly little</h2>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              The consensus logic in MonitorProcess is ~40 lines:
            </p>
            <pre tabIndex={0} className="mb-4 overflow-x-auto rounded-lg border border-border bg-muted p-4 text-sm leading-relaxed"><code className="text-status-up">{`defmodule Uptrack.Monitoring.MonitorProcess do
  use GenServer

  # After local check completes, broadcast to pg group
  def handle_info({:check_result, result}, state) do
    # Broadcast our result to all regions
    :pg.get_members(:monitor_checks, state.monitor_id)
    |> Enum.each(&send(&1, {:region_result, @region, result}))

    state = %{state |
      region_results: Map.put(state.region_results, @region, result),
      checking: false
    }
    maybe_evaluate_consensus(state)
  end

  # Receive result from another region
  def handle_info({:region_result, region, result}, state) do
    state = %{state |
      region_results: Map.put(state.region_results, region, result)
    }
    maybe_evaluate_consensus(state)
  end

  defp maybe_evaluate_consensus(state) do
    results = state.region_results
    total = map_size(results)
    expected = length(:pg.get_members(:monitor_checks, state.monitor_id))

    if total >= min(expected, 2) do
      # Enough results — compute consensus
      down_count = Enum.count(results, fn {_, r} -> r.status == "down" end)
      consensus = if down_count > total / 2, do: "down", else: "up"

      state = %{state |
        last_check: %{status: consensus},
        region_results: %{}  # reset for next cycle
      }
      |> evaluate_result()
      |> record_result()
      |> maybe_trigger_alert()

      {:noreply, state}
    else
      # Still waiting for more regions
      {:noreply, state}
    end
  end
end`}</code></pre>
            <p className="text-lg leading-[1.7] text-body">
              That's the core of multi-region consensus. No Kafka, no Redis, no external coordinator. Just processes sending messages to each other — the thing the BEAM was literally built to do.
            </p>
          </section>

          {/* What we learned */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">What we learned</h2>
            <div className="space-y-4">
              <div className="flex gap-3">
                <span className="mt-1 shrink-0 text-primary">1.</span>
                <p className="text-lg leading-[1.7] text-body">
                  <strong className="text-foreground">The BEAM's distribution primitives are underused.</strong> Most Elixir apps treat nodes as independent units behind a load balancer. But pg, :global, and Erlang distribution enable architectures that would require Kafka + Redis + ZooKeeper in other ecosystems.
                </p>
              </div>
              <div className="flex gap-3">
                <span className="mt-1 shrink-0 text-primary">2.</span>
                <p className="text-lg leading-[1.7] text-body">
                  <strong className="text-foreground">Don't put coordination in the database.</strong> We tried DB-based consensus first. The staleness problem alone killed it. pg messages are real-time with zero staleness.
                </p>
              </div>
              <div className="flex gap-3">
                <span className="mt-1 shrink-0 text-primary">3.</span>
                <p className="text-lg leading-[1.7] text-body">
                  <strong className="text-foreground">The Discord/WhatsApp pattern scales to monitoring.</strong> One process per entity with self-scheduling and in-memory state — it works for chat guilds, phone calls, and uptime monitors.
                </p>
              </div>
              <div className="flex gap-3">
                <span className="mt-1 shrink-0 text-primary">4.</span>
                <p className="text-lg leading-[1.7] text-body">
                  <strong className="text-foreground">$23/mo can compete with $41/mo.</strong> UptimeRobot charges $41/mo for 30-second checks (Team plan, billed monthly). We do it on three $8-10 VPS nodes with better consensus logic.
                </p>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="rounded-lg border border-border bg-muted p-8 text-center">
            <h2 className="font-sans mb-3 text-xl font-semibold">Try it yourself</h2>
            <p className="mb-6 text-lg leading-[1.7] text-body">
              All monitors at 30-second checks, from $3/mo. Multi-region consensus on every paid plan. 30-day money-back guarantee.
            </p>
            <Button size="lg" asChild>
              <Link to="/pricing">
                See Plans
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </section>
        </article>
      </>

      </>
  )
}
